import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, CardBody, CardHeader, Card, Button } from "reactstrap";
import Form from "src/Components/Form";
import { dynamicNotification } from "src/utils/Notifications";
import __ from "src/utils/Translations";
import useIsImpersonated from "src/utils/hooks/useIsImpersonated";
import {
  API_TOTALREWARD_SERVICE,
  restApiRequestFormData,
  getFileNameFromResponse,
  saveFile,
} from "src/utils/Api";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";

export default function ImportForm({ companyId, imported, setImported }) {
  const isImpersonated = useIsImpersonated();
  const submitMethod = async (data) => {
    try {
      const fileToSend = data.file[0];
      const formData = new FormData();
      formData.append("file", fileToSend);
      formData.append("tenant", companyId);

      const result = await restApiRequestFormData(
        API_TOTALREWARD_SERVICE,
        `/omb/imports?tenant=${companyId}`,
        "POST",
        formData,
        {},
        {}
      );

      const newData = await result.json();

      setImported(newData?.id);

      dynamicNotification(
        __(
          "Plik został zlecony do importu. Odśwież stronę i sprawdź wynik operacji w tabeli poniżej."
        )
      );
    } catch (e) {
      dynamicNotification(e.message || __("Wystąpił nieznany błąd"));
      newRelicErrorReport(
        e,
        "Pages/Company/TotalReward/ImportExport/Import/form.js - 21"
      );
    }
  };

  const [data, updateData] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const onChange = (key, value) => {
    const updatedData = { ...data };
    updatedData[key] = value;
    updateData(updatedData);
  };

  const fields = [
    {
      id: "file",
      type: "file",
      label: "Plik Excel",
      validation: [
        "required",
        { method: "allowedExtensions", args: [["xls", "xlsx"]] },
      ],
    },
    {
      key: "submitButton",
      component: (
        <Button
          data-t1="import"
          key="import-submit"
          disabled={isImpersonated || isProcessing}
          color="primary"
          type="submit"
        >
          {__("Importuj")}
        </Button>
      ),
    },
  ];

  const getExcelTemplate = async () => {
    try {
      const response = await restApiRequestFormData(
        API_TOTALREWARD_SERVICE,
        `/omb/import/template`,
        "POST",
        "",
        null,
        { headers: { "Content-Type": "application/json" } }
      );

      const blob = await response.blob();
      const resultFileName =
        getFileNameFromResponse(response) ||
        `total_reward_import_template.xlsx`;
      saveFile(blob, false, resultFileName);
    } catch (e) {
      dynamicNotification(e.message || __("Wystąpił nieznany błąd"));
      newRelicErrorReport(
        e,
        "Pages/Company/TotalReward/ImportExport/Import/form.js - 87"
      );
    }
  };

  return (
    <Card className="main-card mb-3">
      <CardHeader>{__("Import danych")}</CardHeader>
      <CardBody className="m-2">
        <Alert color="warning">
          <p>
            {__(
              "Aby zaimportować benefity na konta użytkowników zalecane jest pobranie szablonu importu lub eksportu danych i przygotowanie nowego pliku do importu na bazie pobranego szablonu. W pliku obowiązkowe jest uzupełnienie czterech kolumn:"
            )}
            <ol>
              <li>{__("ID pracownika = FK pracownika")}</li>
              <li>{__("ID benefitu")}</li>
              <li>{__("Okres (wymagany format: RRRR-MM)")}</li>
              <li>
                {__(
                  "Wartość dla benefitu finansowego lub Opis dla benefitu niefinansowego."
                )}
              </li>
            </ol>
            <div>
              {__(
                "Pozostałe kolumny są jedynie pomocnicze i nie są importowane do systemu."
              )}
            </div>
          </p>
          <p>
            {__("Aby zaimportować na konto pracownika benefit:")}
            <ul>
              <li>
                {__(
                  "z wartością (finansowy) należy w kolumnie „Wartość dla benefitu finansowego” wpisać odpowiednią wartość, natomiast kolumnę „Opis dla benefitu niefinansowego” pozostawić pustą."
                )}
              </li>
              <li>
                {__(
                  "bez wartości (niefinansowy) należy w kolumnie „Opis dla benefitu niefinansowego” wstawić opis, liczbę lub kropkę, natomiast kolumnę „Wartość dla benefitu finansowego” pozostawić pustą."
                )}
              </li>
            </ul>
            <div>
              {__(
                "Aby zmienić wartość lub opis danego benefitu na koncie pracownika należy ponownie wgrać plik z nowymi danymi, które zastąpią poprzednie."
              )}
            </div>
          </p>
          <p>
            {__("W celu usunięcia benefitu:")}
            <ul>
              <li>
                {__(
                  "z wartością (finansowego) należy wprowadzić wartość 0 w kolumnie „Wartość dla benefitu finansowego”,"
                )}
              </li>
              <li>
                {__(
                  "bez wartości (niefinansowego) należy wprowadzić 0 w kolumnie „Opis dla benefitu niefinansowego”."
                )}
              </li>
            </ul>
          </p>

          <hr />
          <Button
            data-t1="export-empty"
            key="export-empty"
            color="secondary"
            type="button"
            onClick={() => getExcelTemplate()}
          >
            {__("Pobierz szablon importu")}
          </Button>
        </Alert>
        <div className="mb-4">
          <Form
            id="importForm"
            data={data}
            config={{
              defaultOnChange: onChange,
              noCards: true,
              onSubmit: async () => {
                if (!isImpersonated) {
                  setIsProcessing(true);
                  await submitMethod(data);
                  setIsProcessing(false);
                  updateData({});
                }
              },
              formGroups: [
                {
                  formElements: fields,
                },
              ],
            }}
          />
        </div>
      </CardBody>
    </Card>
  );
}

ImportForm.propTypes = {
  companyId: PropTypes.string.isRequired,
  imported: PropTypes.string,
  setImported: PropTypes.func,
};

ImportForm.defaultProps = {
  imported: null,
  setImported: () => {},
};
